import { AssignmentInd, CalendarMonth, CheckCircleOutline, Circle, Done, EventAvailable, Person, SubdirectoryArrowRight } from '@mui/icons-material'
import { Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Popover, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { IChangelog, ITask } from '../../definitions/Workflow'
import Tile from '../layout/Tile'
import { snackbar } from '../services/SnackbarService'
import { useEffect, useRef, useState } from 'react'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Helper from '../../helpers/Helper'
import confetti from 'canvas-confetti'
import _ from 'lodash'
import Formatter from '../../helpers/Formatter'
import TimeHelper from '../../helpers/TimeHelper'
import { IUser } from '../../definitions/Users'
import UsersManager from '../../managers/UsersManager'
import DSTE from '../../core/dste/DSTE'

interface P {
    tasks: { task: ITask, startDate: Date, endDate: Date }[]
    isPiP?: boolean
    onChange?: (newTasks: ITask[], newChangelogs: IChangelog[]) => void
}

export default function CurrentTask({ tasks, isPiP, onChange }: P) {
    const theme = useTheme()
    const [selectedAssigneeId, setSelectedAssigneeId] = useState(null)
    const [assigneeChanged, setAssigneeChanged] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    const { status: usersStatus, response: allUsers } = useQuery({
        queryKey: buildQueryKey('all-users'),
        queryFn: async () => await UsersManager.getAllUsers()
    })

    const { response: currentUser } = useQuery({
        queryKey: buildQueryKey('current-user'),
        queryFn: async () => await UsersManager.getCurrentUser()
    })

    const currentTaskIndex = tasks.findIndex(x => x.task.status === 'open')
    const currentTaskData = tasks[currentTaskIndex]
    const currentTask = currentTaskData?.task
    const numberTasks = tasks.length ?? 0
    const isPopoverOpen = Boolean(anchorEl)

    useEffect(() => {
        if (currentTask?.assignee) {
            setSelectedAssigneeId(currentTask?.assignee?.uuid)
        } else {
            setSelectedAssigneeId('')
        }
    }, [currentTask])

    const handleReassignClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const closePopover = () => {
        setAnchorEl(null)
        setSelectedAssigneeId(currentTask?.assignee?.uuid || '')
        setAssigneeChanged(false)
    }

    const handleSaveChanges = async () => {
        const updatedAssignee: IUser = allUsers.find(user => user.uuid === selectedAssigneeId)

        if (updatedAssignee && currentTask) {
            const updatedTasks = tasks.map(({ task, startDate, endDate }) =>
                task.uuid === currentTask.uuid
                    ? { task: { ...task, assignee: updatedAssignee }, startDate, endDate }
                    : { task, startDate, endDate }
            )

            const changelogData: IChangelog = {
                uuid: Helper.generateUUID('changelog_'),
                activity: 'task',
                activityType: 'assigned',
                before: { uuid: currentTask.uuid, assignee: currentTask.assignee },
                after: { uuid: currentTask.uuid, assignee: updatedAssignee },
                createdAt: Formatter.date(DSTE.now(), 'iso'),
                user: currentUser
            }
            onChange(updatedTasks.map(({ task }) => task), [changelogData])
            snackbar.show({ severity: 'success', text: `Task reassigned to ${updatedAssignee.name}` })
            closePopover()
        }
    }

    const onTaskStatusChange = async (uuid: string) => {
        const newTasks = structuredClone(tasks)
        const taskData = newTasks.find(({ task }) => task.uuid === uuid)

        if (taskData) {
            const { task } = taskData
            const beforeChange = _.cloneDeep(task)

            task.status = 'completed'
            task.completedAt = task.status === 'completed' ? Formatter.date(new Date(), 'iso') : ''
            const activityType = 'complete'
            const changelogData: IChangelog = {
                uuid: Helper.generateUUID('log_'),
                activity: 'task',
                activityType: activityType,
                before: { uuid: task.uuid, task: beforeChange },
                after: { uuid: task.uuid, task: { ...task } },
                createdAt: Formatter.date(new Date(), 'iso'),
                user: currentUser
            }
            onChange(newTasks.map(({ task }) => task), [changelogData])
        }
    }

    const triggerConfetti = () => {
        if (buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect()

            const buttonPosition = {
                particleCount: 250,
                spread: 360,
                origin: {
                    x: (buttonRect.left + buttonRect.width / 2) / window.innerWidth,
                    y: (buttonRect.top + buttonRect.height / 2) / window.innerHeight
                },
                zIndex: 9999
            }

            confetti(buttonPosition)
            confetti({
                particleCount: 250,
                spread: 120,
                origin: { x: 0, y: 1 },
                zIndex: 9999
            })
            confetti({
                particleCount: 250,
                spread: 120,
                origin: { x: 1, y: 1 },
                zIndex: 9999
            })
        }
    }

    // const renderForPiP = () => {
    //     // TODO: move all the logic that checks on the PiP here
    //     if (!isPiP) return null

    //     // TODO: do here
    //     return null
    // }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{
                flex: 1,
                marginTop: 0,
                padding: isPiP ? 0 : theme.spacing(1),
                display: 'flex',
                flexDirection: 'column' }}>
                <Box sx={{ marginTop: theme.spacing(1), flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                        marginTop={1}
                        variant='h5'
                        fontWeight='bold'>
                        {currentTask?.title}
                    </Typography>
                    <Typography marginTop={1} variant='body1' fontSize={12}>
                        {tasks.every(task => task.task.status === 'completed')
                            ? (
                                    'All tasks completed'
                                )
                            : (
                                    <>
                                        Task
                                        {' '}
                                        <Box component='span' fontWeight='bold'>{currentTaskIndex + 1}</Box>
                                        {' '}
                                        of
                                        {' '}
                                        {numberTasks}
                                    </>
                                )}
                    </Typography>
                    {/* check comment above */}
                    {!isPiP && (
                        <Tile.Row spacing={2} flex={0} alignItems='center' style={{ marginTop: theme.spacing(2) }}>
                            <CheckCircleOutline />
                            <Typography variant='body2' width={150}>Status</Typography>
                            <Chip
                                avatar={(
                                    <Avatar>
                                        <Circle fontSize='small' color={currentTask?.status === 'open' ? 'secondary' : 'success'} />
                                    </Avatar>
                                )}
                                label={currentTask?.status === 'open' ? 'Open' : 'Completed'}
                                color={currentTask?.status === 'open' ? 'primary' : 'success'}
                                size='small'
                            />
                        </Tile.Row>
                    )}
                    {!isPiP && (
                        <Tile.Row spacing={2} flex={0} alignItems='center' style={{ marginTop: theme.spacing(2) }}>
                            <EventAvailable />
                            <Typography variant='body2' width={150}>Days to complete</Typography>
                            {Helper.hasVal(currentTask?.daysToComplete) && (
                                <Typography
                                    variant='body2'
                                    fontWeight='bold'
                                    noWrap>
                                    {currentTask?.daysToComplete} {Number(currentTask?.daysToComplete) === 1 ? 'day' : 'days'}
                                </Typography>
                            )}
                        </Tile.Row>
                    )}
                    {!isPiP && (
                        <Tile.Row spacing={2} flex={0} alignItems='center' style={{ marginTop: theme.spacing(2) }}>
                            <CalendarMonth />
                            <Typography variant='body2' width={150}>Current Task Due</Typography>
                            <Typography
                                variant='body2'
                                fontWeight='bold'
                                noWrap>
                                {Formatter.date(new Date(TimeHelper.getWorkingDatePlusDays(new Date(), currentTask?.daysToComplete)), 'long')}
                            </Typography>
                        </Tile.Row>
                    )}
                    {!isPiP && (
                        <Tile.Row spacing={2} flex={0} alignItems='center' style={{ marginTop: theme.spacing(2) }}>
                            <Person />
                            <Typography variant='body2' width={150}>Assigned to</Typography>
                            <Box display='flex' alignItems='center'>
                                {currentTask?.assignee && (
                                    <Tooltip key={'Current task owner' + currentTask.uuid} title={currentTask.assignee.name}>
                                        <img src={currentTask.assignee.image} style={{ height: 25, width: 25, borderRadius: '100%' }} />
                                    </Tooltip>
                                )}
                            </Box>
                        </Tile.Row>
                    )}
                    {!isPiP && (
                        <Tile.Row spacing={2} flex={0} alignItems='center' style={{ marginTop: theme.spacing(2) }}>
                            <SubdirectoryArrowRight />
                            <Typography variant='body2' width={150}>Dependencies</Typography>
                            <Box display='flex' alignItems='center'>
                                {currentTask?.dependencies && (
                                    <Typography variant='body2' fontWeight='bold' width={150}>
                                        {currentTask?.dependencies?.map((x, index) => (
                                            <span key={x.uuid}>
                                                {x.name}
                                                {index < currentTask.dependencies.length - 1 && ', '}
                                            </span>
                                        ))}
                                    </Typography>
                                )}
                            </Box>
                        </Tile.Row>
                    )}
                    <Box style={{ marginTop: theme.spacing(2), flex: 1, overflowY: 'auto' }}>
                        <Typography
                            marginTop={1}
                            variant='body2'
                            sx={{
                                overflowY: 'auto',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: isPiP ? 4 : 'none',
                                textOverflow: isPiP ? 'ellipsis' : undefined,
                                height: isPiP ? 'calc(var(--unit)*10)' : 'calc(var(--unit)*35)',
                                marginBottom: theme.spacing(2)
                            }}>
                            {currentTask?.description}
                        </Typography>
                    </Box>
                    <Box style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(2),
                        margin: theme.spacing(1)
                    }}>
                        {!isPiP && (
                            <Tooltip title='Re-Assign task to other user' arrow>
                                <Button
                                    variant='outlined'
                                    size='medium'
                                    onClick={handleReassignClick}
                                    startIcon={<AssignmentInd />}
                                    disabled={currentTask?.status !== 'open'}>
                                    Re-assign
                                </Button>
                            </Tooltip>
                        )}
                        <Button
                            ref={buttonRef}
                            variant='contained'
                            onClick={() => {
                                if (currentTask.status !== 'completed') {
                                    triggerConfetti()
                                    onTaskStatusChange(currentTask.uuid)
                                    snackbar.show({ severity: 'success', text: 'Task marked as Complete' })
                                }
                            }}
                            size='medium'
                            startIcon={<Done />}
                            disabled={!Helper.hasVal(currentTask) || currentTask?.status === 'completed'}>
                            Mark as Complete
                        </Button>
                    </Box>
                </Box>
            </div>
            <div style={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'flex-end'
            }}>
                <Popover
                    style={{ padding: theme.spacing(2) }}
                    open={isPopoverOpen}
                    anchorEl={anchorEl}
                    onClose={closePopover}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Box sx={{ padding: theme.spacing(1) }}>
                        {usersStatus === 'loading'
                            ? <CircularProgress />
                            : usersStatus === 'error'
                                ? <Typography color='error'>Failed to load users</Typography>
                                : (
                                        <Autocomplete
                                            options={allUsers}
                                            getOptionLabel={user => user.name}
                                            value={allUsers.find(user => user.uuid === selectedAssigneeId) || null}
                                            onChange={(event, newValue) => {
                                                if (newValue) {
                                                    setSelectedAssigneeId(newValue.uuid)
                                                    setAssigneeChanged(true)
                                                }
                                            }}
                                            renderInput={params => <TextField {...params} label='Select User' variant='outlined' />}
                                        />
                                    )}
                    </Box>
                    <Box
                        display='flex'
                        gap={1}
                        padding={1}
                        justifyContent='flex-end'>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={handleSaveChanges}
                            disabled={!assigneeChanged || usersStatus === 'loading'}>
                            Save Changes
                        </Button>
                        <Button variant='outlined' onClick={closePopover}>
                            Cancel
                        </Button>
                    </Box>
                </Popover>
            </div>
        </div>
    )
}
